import * as React from 'react';
import { FrontLayout } from 'client/ui/layout/FrontLayout';
import { Link, RouteComponentProps } from 'react-router-dom';
import { Typography, Space } from 'antd';
import { LabeledDate } from 'client/ui/display/date/LabeledDate';
import { RequestApi } from 'client/components/schema/requests/RequestApi';
import { AlertPage } from 'client/ui/alert/AlertPage';
import { getNetworkErrorMessage } from 'client/core/network/errors/getNetworkErrorMessage';
import { useApiQuery } from 'client/core/network/hooks/useApiQuery';
import { LoadingPage } from 'client/ui/loader/LoadingPage';
import { useBreadcrumbItem } from 'client/core/router/breadcrumb/BreadcrumbContext';
import { useRequestDocumentTree } from './useRequestDocumentTree';
import { PageFullRow } from 'client/ui/layout/page/PageFullRow';
import { PageSecondaryColumn } from 'client/ui/layout/page/PageSecondaryColumn';
import { ProcedureSummaryTitle } from '../../procedures/single/shared/ProcedureSummaryTitle';
import { ProcedureLinkText } from 'client/ui/naming/ProcedureLinkText';
import { RequestDossierMainColumn } from './RequestDossierMainColumn';
import { ProcedureLogic } from 'common/schema/ProcedureLogic';

interface Params {
  id: string;
}

/**
 * Pagina per la visualizzazione del Fascicolo del Documento
 * con i dettagli per gli esiti e i documenti pubblici di una richiesta.
 * Visibile solo per gli utenti autenticati.
 *
 * */
export interface RequestDossierPageProps extends RouteComponentProps<Params> {}

export function RequestDossierPage(props: RequestDossierPageProps) {
  const id = props.match.params.id ? parseInt(props.match.params.id, 10) : null;

  const { response, error, loading } = useApiQuery(RequestApi.find, {
    skip: id == null,
    data: {
      id: id!
    }
  });
  const request = response?.data;
  const documentTree = useRequestDocumentTree(request?.id);

  useBreadcrumbItem({ path: '/richieste', title: 'Le mie Istanze' });
  useBreadcrumbItem({
    path: `/richieste/${id}/dettaglio`,
    title: 'Dettagli'
  });
  useBreadcrumbItem({ path: props.location.pathname, title: 'Fascicolo' });

  if (error) {
    return (
      <AlertPage
        showIcon
        type="error"
        message="Impossibile caricare l'istanza"
        description={getNetworkErrorMessage(error)}
      />
    );
  }

  if (documentTree.error) {
    return (
      <AlertPage
        showIcon
        type="error"
        message="Impossibile caricare l'istanza"
        description={getNetworkErrorMessage(documentTree.error)}
      />
    );
  }

  if (!request || loading) return <LoadingPage />;

  return (
    <FrontLayout isUserPage>
      <PageFullRow>
        {/* Colonna Principale */}
        <RequestDossierMainColumn
          documentTree={documentTree}
          id={id}
          request={request}
          backText="Torna al dettaglio"
          backLink={`/richieste/${id}/dettaglio`}
        />

        {/* Colonna Sommario / Azioni  */}
        <PageSecondaryColumn orderMobile={3}>
          <Space direction="vertical" size={16}>
            <ProcedureSummaryTitle children={request.procedure.title} />
            <LabeledDate
              label="Scadenza"
              date={ProcedureLogic.getRenderableDateWith24Hour(
                request.procedure.expiresAt
              )}
            />
            <Link to={`/domande/${request.procedure.id}`}>
              {ProcedureLinkText}
            </Link>
            {/* <Link to={''}>Vai alle Faq</Link> */}
          </Space>
        </PageSecondaryColumn>
      </PageFullRow>
    </FrontLayout>
  );
}

// Per test e Design
// TODO: Estendere Tabella Output per inserire dati di visualizzazione
// che saranno impostati in un Tab dedicato lato Admin (Fascicolo??) :
// visible: boolean (default false) = Indica se il campo sarà mostrato nel dettaglio (solo se valorizzato)
// Andrà configurato anche la scelta del campo del documentale (output) che andrà visualizzato in evidenza nella pagina (come status)
